import { useRef } from "react";
import { Box, Card, Typography } from "@mui/material";
import CustomTypography from "../../../Components/Typography";
import CustomCheckboxLabel from "../../../Components/CustomCheckboxLabel";
import TimeInput from "./TimeInput";
import muiStyles from "../styles/muiStyles";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function DayRow({ index, dayKey, isloading }) {
  const { t } = useTranslation("page-business-settings");
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();

  const day = values.openingHours?.[index] ?? {
    dayOfWeek: index + 1,
    enabled: false,
    openTime: null,
    closeTime: null,
  };

  // The day-level "range" Yup test puts its failure message as a string at
  // errors.openingHours[index]. Field-level errors (e.g. invalid HH:MM) end up
  // at errors.openingHours[index].openTime / closeTime.
  const dayErrorRaw = errors?.openingHours?.[index];
  const dayErrorString = typeof dayErrorRaw === "string" ? dayErrorRaw : null;
  const fieldErrors = typeof dayErrorRaw === "object" && dayErrorRaw !== null
    ? dayErrorRaw
    : {};
  const dayTouched = !!touched?.openingHours?.[index];

  const updateDay = (patch) => {
    const next = [...(values.openingHours || [])];
    next[index] = { ...day, ...patch };
    // Reset touched on every enabled toggle. Submit marks every path touched,
    // so without this, checking a day after a save would immediately surface
    // a stale error before the user has actually tried to fill the times.
    if ("enabled" in patch) {
      setFieldTouched(`openingHours.${index}`, false, false);
    }
    // Validate after the change so stale errors clear immediately — e.g. when
    // the user unchecks a previously-invalid day, the range error disappears.
    setFieldValue("openingHours", next, true);
  };

  const rowRef = useRef(null);

  // Touched is computed at the row level: only when focus actually leaves
  // the entire day row (i.e. the user is done with this day). Moving focus
  // From → End within the same row should not mark touched, so the user
  // isn't shown a "missing closing time" error mid-fill.
  const handleRowBlur = (e) => {
    if (
      rowRef.current &&
      e.relatedTarget instanceof Node &&
      rowRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    setFieldTouched(`openingHours.${index}`, true, false);
  };

  const showRangeError = dayTouched && dayErrorString;
  const showOpenError = dayTouched && fieldErrors.openTime;
  const showCloseError = dayTouched && fieldErrors.closeTime;

  return (
    <Box
      ref={rowRef}
      onBlur={handleRowBlur}
      sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "120px" }}>
          <CustomCheckboxLabel
            checked={!!day.enabled}
            onChange={(e) => updateDay({ enabled: e.target.checked })}
            disabled={isloading}
          />
          <Typography sx={{ fontSize: "15px" }}>{t(`days.${dayKey}`)}</Typography>
        </Box>

        {/* Group From / "-" / To together so they wrap as one unit on narrow
            viewports instead of breaking between the dash and the second input. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <TimeInput
            value={day.openTime}
            onChange={(v) => updateDay({ openTime: v })}
            disabled={isloading || !day.enabled}
            placeholder={t("time_from")}
            error={!!(showRangeError || showOpenError)}
          />
          <Typography sx={{ fontSize: "15px" }}>-</Typography>
          <TimeInput
            value={day.closeTime}
            onChange={(v) => updateDay({ closeTime: v })}
            disabled={isloading || !day.enabled}
            placeholder={t("time_to")}
            error={!!(showRangeError || showCloseError)}
          />
        </Box>
      </Box>

      {(showRangeError || showOpenError || showCloseError) && (
        <Typography sx={{ color: "#d32f2f", fontSize: "12px", ml: "120px" }}>
          {showRangeError || showOpenError || showCloseError}
        </Typography>
      )}
    </Box>
  );
}

const OpeningHoursWorkingDays = ({ isloading }) => {
  const { t } = useTranslation("page-business-settings");
  const { cardStyles, fieldsLabelLightStyles } = muiStyles;

  return (
    <Card sx={cardStyles}>
      <CustomTypography type="subheading">
        {t("opening_hours_heading")}
      </CustomTypography>
      <Typography color="rgba(47, 43, 61, 0.9)" sx={{ ...fieldsLabelLightStyles, mt: "4px" }}>
        {t("opening_hours_subtext")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          // Use auto columns so each day row sits at its natural width and the
          // two columns don't end up split across the whole card.
          gridTemplateColumns: { xs: "1fr", md: "auto auto" },
          justifyContent: "start",
          rowGap: "16px",
          columnGap: "32px",
          mt: "24px",
        }}
      >
        {DAY_KEYS.map((dayKey, idx) => (
          <DayRow key={dayKey} index={idx} dayKey={dayKey} isloading={isloading} />
        ))}
      </Box>
    </Card>
  );
};

export default OpeningHoursWorkingDays;
